import { useActionBarContext } from "@/components/contexts/action-bar-context";
import { useAPIEditorTools } from "@/components/contexts/api-editor-context-hooks";
import { FormLabelRequired } from "@/components/form-label-required";
import { MdTextarea } from "@/components/md-textarea";
import { ActionBarContent } from "@/components/module-action-bar/action-bar-content";
import { EditorInputProps } from "@/components/module-api-editor/types";
import {
  BtnGroup,
  SubmitButton,
} from "@/components/module-visual-editor/shared-components";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FieldHStack,
  FieldVStack,
  FormComp,
  FormContent,
  FormHeader,
  FormTitle,
} from "@/forms";
import { useDisclosure } from "@/hooks/use-disclosure";
import { useOperation } from "@/hooks/use-operation";
import { useTags } from "@/hooks/use-tags";
import { supportedHttpVerbs } from "@/lib/helpers";
import { isReference } from "@/lib/oas-tools/oas-tag-helpers";
import { appRegex } from "@/lib/regex";
import {
  OASDefinition,
  OASOperation,
  OASParameter,
  SupportedHTTPVerbs,
} from "@/lib/types";
import { getParametersByType, getParametersFromUrlPath } from "@/lib/url-path";
import {
  cn,
  DEFAULT_ICON_SIZE,
  NormIcons,
  toastError,
  truncateText,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import camelCase from "lodash/camelCase";
import merge from "lodash/merge";
import { useMemo, useState } from "react";
import { DefaultValues, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const pathInputFormSchema = z.object({
  urlPath: z
    .string()
    .min(1, "Required")
    .max(500)
    .regex(appRegex.urlPath.expression, appRegex.urlPath.message),
  description: z.string().optional(),
  summary: z.string().min(1, "Required").max(50),
  method: z.enum(supportedHttpVerbs, { message: "Select method" }),
  tag: z.string().optional(),
});
export type OperationFormValues = z.infer<typeof pathInputFormSchema>;

export function OperationForm({
  defaultValues = {},
  onSubmit,
  title,
  btnTitle,
  value,
  onChange,
}: {
  defaultValues?: Partial<OperationFormValues>;
  onSubmit: SubmitHandler<OperationFormValues>;
  title: string;
  btnTitle: string;
} & EditorInputProps) {
  const {
    findAllTags: getAll,
    findTag: get,
    create,
  } = useTags({ value, onChange });

  const commandDisclosure = useDisclosure();

  const allTags = useMemo(() => getAll(), [getAll]);

  const form = useForm<OperationFormValues>({
    resolver: zodResolver(pathInputFormSchema),
    defaultValues: merge(
      {
        urlPath: "",
        method: "",
        operationId: "",
        summary: "",
        tag: "",
      },
      defaultValues
    ),
  });

  const onCreateTag = (tagName: string) => {
    const existing = get(tagName);
    if (existing) toast.error(`Tag with the name or ${tagName} already exists`);
    try {
      create({ name: tagName });
      form.setValue("tag", tagName.toLowerCase());
      commandDisclosure.onClose();
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  const [query, setQuery] = useState("");

  return (
    <Form {...form}>
      <FormComp onSubmit={form.handleSubmit((values) => onSubmit(values))}>
        <FormHeader>
          <FormTitle>{title}</FormTitle>
        </FormHeader>
        <FormContent>
          <FieldHStack>
            <FormField
              control={form.control}
              name="method"
              render={({ field }) => {
                return (
                  <FormItem className="grow">
                    <FormLabel>
                      <FormLabelRequired />
                      Method
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {supportedHttpVerbs.map((v) => (
                          <SelectItem key={v} value={v}>
                            {v.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="tag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tag</FormLabel>
                  <Popover
                    open={commandDisclosure.isOpen}
                    onOpenChange={commandDisclosure.onOpenChange}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "h-10 w-full justify-between flex",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? allTags.find((tag) => {
                                return tag.name === field.value;
                              })?.name || "Tag not found"
                            : "Select tag"}
                          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[250px] p-0">
                      <Command>
                        <CommandInput
                          placeholder="Search tag..."
                          className="h-9"
                          value={query}
                          onValueChange={(v) => setQuery(v)}
                        />
                        <CommandList>
                          {!query && (
                            <CommandEmpty className="py-3 text-muted-foreground text-xs text-center">
                              Type to create a tag
                            </CommandEmpty>
                          )}
                          <CommandGroup>
                            {allTags.map((tag) => (
                              <CommandItem
                                value={tag.name}
                                key={tag.name}
                                onSelect={() => {
                                  form.setValue("tag", tag.name);
                                  commandDisclosure.onClose();
                                }}
                              >
                                {tag.name}
                                <CheckIcon
                                  className={cn(
                                    "ml-auto h-4 w-4",
                                    tag.name === field.value
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {query &&
                            !allTags.some(
                              (t) => t.name === query.toLowerCase()
                            ) && (
                              <div className="flex justify-end py-1 px-1">
                                <Button
                                  size="xs"
                                  className="w-full mt-1 mx-1"
                                  onClick={() => onCreateTag(query)}
                                >
                                  <NormIcons.Add
                                    size={DEFAULT_ICON_SIZE}
                                    className="mr-2"
                                  />
                                  {`Create tag "${truncateText(query, 10)}"`}
                                </Button>
                              </div>
                            )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldHStack>
          <FieldVStack>
            <FormField
              control={form.control}
              name="urlPath"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormLabelRequired />
                    Path name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="/cats/{cat_id}/foods" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>
                      <FormLabelRequired />
                      Summary
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      Use a short description like "Get book" or "Create author"
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <MdTextarea height={130} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldVStack>
        </FormContent>
        <BtnGroup className="justify-end">
          <SubmitButton>{btnTitle}</SubmitButton>
        </BtnGroup>
      </FormComp>
    </Form>
  );
}

export function OperationAdd({
  value,
  onChange,
  defaultValues,
}: EditorInputProps & { defaultValues?: DefaultValues<OperationFormValues> }) {
  const [, dispatch] = useActionBarContext();
  const { addOperationAndParameters } = useOperation({
    value,
    onChange,
  });
  const { setActiveElement } = useAPIEditorTools({ value, onChange });

  const onSubmit = async ({
    method,
    urlPath,
    summary,
    tag,
  }: OperationFormValues) => {
    const operationId = camelCase(summary);

    const operation: OASOperation = {
      operationId,
      tags: tag ? [tag] : undefined,
      summary,
    };

    try {
      addOperationAndParameters({ urlPath, method, operation });
      dispatch({
        type: "SET_PAGE",
        payload: {
          name: "suggested-responses",
          context: {
            urlPath,
            method,
            operationId,
          },
        },
      });
      await setActiveElement({
        type: "operation",
        operationId: operationId,
      });
    } catch (err) {
      toastError(err);
    }
  };
  return (
    <ActionBarContent>
      <OperationForm
        defaultValues={defaultValues}
        title="Create operation"
        btnTitle="Create"
        onSubmit={onSubmit}
        value={value}
        onChange={onChange}
      />
    </ActionBarContent>
  );
}

// The intent here is to keep parameters that are NOT references but
// STILL exist in the urlPath
function mergeParameters({
  newUrlPath,
  operation,
  definition,
}: {
  operation: OASOperation;
  newUrlPath: string;
  definition: OASDefinition;
}) {
  const res: OASParameter[] = [
    ...getParametersByType(operation, "query", definition),
  ];

  const existingPathParameters = getParametersByType(
    operation,
    "path",
    definition
  );

  const pathParams = getParametersFromUrlPath(newUrlPath);

  existingPathParameters.forEach((existingPathParam) => {
    if (isReference(existingPathParam)) return;
    if (
      pathParams.some((pathParam) => pathParam.name === existingPathParam.name)
    ) {
      res.push(existingPathParam);
    }
  });

  return res;
}

export function OperationEdit({
  urlPath: oldUrlPath,
  method: oldMethod,
  value,
  onChange,
}: {
  urlPath: string;
  method: SupportedHTTPVerbs;
} & EditorInputProps) {
  const [, dispatch] = useActionBarContext();
  const { setActiveElement } = useAPIEditorTools({ value, onChange });
  const { editOperationAndParams, findOperation: getOperation } = useOperation({
    value,
    onChange,
  });
  const operation = getOperation(oldUrlPath, oldMethod);
  if (!operation) throw new Error("Trying to edit non-existant operation");

  const onSubmit = async ({
    method,
    urlPath,
    description,
    summary,
    tag,
  }: OperationFormValues) => {
    if (urlPath.endsWith("/")) urlPath = urlPath.slice(0, -1);
    try {
      const operationId = camelCase(summary);
      dispatch({
        type: "CLOSE",
      });
      await editOperationAndParams({
        oldUrlPath,
        oldMethod,
        newUrlPath: urlPath,
        newMethod: method,
        newOperation: {
          ...operation,
          parameters: mergeParameters({
            newUrlPath: urlPath,
            operation,
            definition: value.data,
          }),
          operationId,
          description,
          summary,
          tags: tag ? [tag] : undefined,
        },
      });
      await setActiveElement({ type: "operation", operationId });
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    }
  };
  return (
    <ActionBarContent>
      <OperationForm
        onSubmit={onSubmit}
        defaultValues={{
          urlPath: oldUrlPath,
          method: oldMethod,
          description: operation.description,
          summary: operation.summary,
          tag: operation?.tags?.length ? operation.tags[0] : undefined,
        }}
        title="Edit operation"
        btnTitle="Edit"
        value={value}
        onChange={onChange}
      />
    </ActionBarContent>
  );
}
